/**
 * rightSection.js — ОТКРЫТЬ РАЗДЕЛ ПРАВОГО САЙДБАРА КНОПКОЙ СВЕРХУ. [12.0.25]
 *
 * Решения — в rightSectionCore.js (под тестами), здесь только страница.
 *
 * Открытие сайдбара и переключение вкладок НЕ переписаны: зовём те же
 * кнопки #btn-sidebar-right-toggle и .right-tab[data-tab], чьи обработчики
 * живут в htmlInit.js и sidebarBindings.js. Так делали модули RT и
 * «Качество» и раньше — каждый своей копией из трёх строк. Теперь копия
 * одна, и к ней добавилось то, чего не было ни у кого: прокрутка раздела к
 * началу и разовая отметка «раздел открылся здесь».
 *
 * Отметка — класс .tab-arrive на теле вкладок и на кнопке вкладки на
 * ОТМЕТКА_МС; вид задают правила «ОТМЕТКА ПРИБЫТИЯ» в index.html. Щелчок по
 * любой вкладке гасит отметку сразу: врач ушёл в другой раздел, и рамка на
 * общем теле вкладок подсветила бы уже не тот.
 *
 * EXPORTS: открытьРазделСправа, закрытьСайдбарСправа, следитьЗаКнопкой
 */

import { планОткрытия, кнопкаГорит, ОТМЕТКА_МС } from './rightSectionCore.js';

const КЛАСС = 'tab-arrive';
let _пуск = null;
let _снять = null;
let _гасительПривязан = false;

const _сайдбар = () => document.getElementById('sidebar-right');
const _открыт = () => !!_сайдбар()?.classList.contains('floating-open');
const _тело = () => document.querySelector('#sidebar-right .right-tab-body');
const _кнопкаВкладки = (id) => document.querySelector(`.right-tab[data-tab="${id}"]`);
const _узкий = () => window.innerWidth <= 768;

function _активнаяВкладка() {
  return _сайдбар()?.querySelector('.tab-panel.active')?.id || null;
}

function _погасить() {
  clearTimeout(_пуск); _пуск = null;
  clearTimeout(_снять); _снять = null;
  for (const эл of document.querySelectorAll('.' + КЛАСС)) эл.classList.remove(КЛАСС);
}

function _привязатьГаситель() {
  if (_гасительПривязан) return;
  const ряды = _сайдбар()?.querySelector('.right-tabs-wrapper');
  if (!ряды) return;
  _гасительПривязан = true;
  // Наш собственный щелчок по вкладке случается ДО постановки отметки
  // (открытьРазделСправа щёлкает, потом отмечает), так что гаситель его
  // не задевает — гасит только уход врача в другой раздел.
  ряды.addEventListener('click', (e) => { if (e.target?.closest?.('.right-tab')) _погасить(); }, true);
}

function _отметить(id, задержкаМс) {
  _погасить();
  const пуск = () => {
    _пуск = null;
    const цели = [_тело(), _кнопкаВкладки(id)].filter(Boolean);
    // Снять и поставить заново: анимация на элементе, где класс уже стоит,
    // сама не перезапускается, а второе нажатие должно вспыхнуть снова.
    for (const эл of цели) { эл.classList.remove(КЛАСС); void эл.offsetWidth; эл.classList.add(КЛАСС); }
    _снять = setTimeout(() => { _снять = null; for (const эл of цели) эл.classList.remove(КЛАСС); }, ОТМЕТКА_МС);
  };
  if (задержкаМс > 0) _пуск = setTimeout(пуск, задержкаМс);
  else пуск();
}

/**
 * Открыть раздел правого сайдбара и показать, что он открылся.
 * @param {string} id — id раздела (.tab-panel), например 'tab-rt'
 * @returns {boolean} false — на узком экране: сайдбара нет, показывайте по-своему
 */
export function открытьРазделСправа(id) {
  const план = планОткрытия({ узкий: _узкий(), сайдбарОткрыт: _открыт() });
  if (!план.действовать) return false;
  _привязатьГаситель();
  if (план.открытьСайдбар) document.getElementById('btn-sidebar-right-toggle')?.click();
  // Щелчок и по уже активной вкладке: на нём висят обновления разделов
  // («Отчёты», «Измерения» пересобираются по щелчку), а второй ряд вкладок
  // по нему разворачивается, если был свёрнут (htmlInit.js), — иначе
  // активную вкладку RT было бы не видно.
  _кнопкаВкладки(id)?.click();
  if (план.прокрутитьВверх) { const т = _тело(); if (т) т.scrollTop = 0; }
  if (план.отметка) _отметить(id, план.задержкаМс);
  return true;
}

/** Закрыть правый сайдбар его же кнопкой (один хозяин у состояния). */
export function закрытьСайдбарСправа() {
  _погасить();
  if (_открыт()) document.getElementById('btn-sidebar-right-toggle')?.click();
}

/**
 * Держать кнопку верхнего меню «нажатой», пока её раздел на виду.
 * Следит за СОСТОЯНИЕМ (классы сайдбара и раздела), а не за щелчками —
 * почему, см. кнопкаГорит в rightSectionCore.js.
 * @param {string} id — раздел
 * @param {HTMLElement} кнопка
 * @param {string} [класс='rt-live']
 * @returns {() => void} отписка
 */
export function следитьЗаКнопкой(id, кнопка, класс = 'rt-live') {
  const сб = _сайдбар();
  const раздел = document.getElementById(id);
  if (!кнопка || !сб || !раздел) return () => {};
  const сверить = () => {
    // На узком экране сайдбара нет — там подсветкой ведает сам модуль
    // (плавающая панель RT), и наблюдатель её не трогает.
    if (_узкий()) return;
    кнопка.classList.toggle(класс, кнопкаГорит({
      сайдбарОткрыт: _открыт(), активнаяВкладка: _активнаяВкладка(), раздел: id,
    }));
  };
  const наблюдатель = new MutationObserver(сверить);
  наблюдатель.observe(сб, { attributes: true, attributeFilter: ['class'] });
  наблюдатель.observe(раздел, { attributes: true, attributeFilter: ['class'] });
  сверить();
  return () => наблюдатель.disconnect();
}
